import React, { useState, useEffect } from 'react';
import { useAuth, UserRole } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import DashboardStats from '../components/dashboard/DashboardStats';
import SituationMapCard from '../components/dashboard/SituationMapCard';
import MainCouranteCard from '../components/dashboard/MainCouranteCard';
import { EngagedPersonnelSection, PersonnelDialog, CrisisDirectoryCard } from '../components/dashboard/PersonnelWidgets';
import DashboardFullscreenMap from '../components/dashboard/DashboardFullscreenMap';
import useLiveRefresh from '../hooks/useLiveRefresh';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
    const { token, user } = useAuth();
    const { theme } = useTheme();
    const isChef = user?.role === UserRole.CHEF_DISPOSITIF;
    const [stats, setStats] = useState(null);
    const [incidents, setIncidents] = useState([]);
    const [careCenters, setCareCenters] = useState([]);
    const [directory, setDirectory] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showShifts, setShowShifts] = useState(false);
    const [engaged, setEngaged] = useState([]);
    const [mainCourante, setMainCourante] = useState([]);
    const [dangerZones, setDangerZones] = useState([]);
    const [drawings, setDrawings] = useState([]);
    const [fsMapOpen, setFsMapOpen] = useState(false);

    const fetchData = async () => {
        try {
            const headers = { Authorization: `Bearer ${token}` };
            const [statsRes, incidentsRes, centersRes, directoryRes, vehiclesRes, engagedRes, eventRes, mcRes, zonesRes, drawingsRes] = await Promise.all([
                axios.get(`${API}/dashboard/stats`, { headers }),
                axios.get(`${API}/incidents?active_only=true`, { headers }),
                axios.get(`${API}/care-centers?status=open`, { headers }),
                axios.get(`${API}/crisis-directory`, { headers }),
                axios.get(`${API}/vehicles`, { headers }),
                axios.get(`${API}/shifts/engaged`, { headers }),
                axios.get(`${API}/event/status`, { headers }),
                axios.get(`${API}/main-courante`, { headers }).catch(() => ({ data: [] })),
                axios.get(`${API}/services-techniques/zones-dangereuses`, { headers }).catch(() => ({ data: [] })),
                axios.get(`${API}/map/drawings`, { headers }).catch(() => ({ data: [] }))
            ]);

            // Use pcs_activation_time from event status (most reliable)
            const statsData = statsRes.data;
            if (eventRes.data.pcs_activation_time && !statsData.pcs_activation_time) {
                statsData.pcs_activation_time = eventRes.data.pcs_activation_time;
            }

            setStats(statsData);
            setIncidents(incidentsRes.data);
            setCareCenters(centersRes.data);
            setDirectory(directoryRes.data);
            setVehicles(vehiclesRes.data);
            setEngaged(engagedRes.data);
            setMainCourante(mcRes.data);
            setDangerZones(zonesRes.data);
            setDrawings(drawingsRes.data);
        } catch (error) {
            console.error('Failed to fetch dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleEvent = async () => {
        const action = stats?.event_active !== false ? 'cloturer' : 'activer';
        if (!window.confirm(`Voulez-vous ${action} l'evenement PCS ?`)) return;
        try {
            const res = await axios.put(`${API}/event/toggle`, {}, { headers: { Authorization: `Bearer ${token}` } });
            toast.success(res.data.message);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 30000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    useLiveRefresh(fetchData);

    const mapCenter = incidents.length > 0
        ? [incidents[0].latitude, incidents[0].longitude]
        : careCenters.length > 0
        ? [careCenters[0].latitude, careCenters[0].longitude]
        : [46.603354, 1.888334];

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="dashboard-page">
            <DashboardHeader token={token} stats={stats} isChef={isChef} onToggleEvent={handleToggleEvent} />

            <DashboardStats stats={stats} onShowShifts={() => setShowShifts(true)} />

            {/* Map and Main Courante */}
            <div className={`grid grid-cols-1 gap-6 ${isChef ? 'lg:grid-cols-3' : ''}`}>
                <SituationMapCard
                    theme={theme} mapCenter={mapCenter}
                    incidents={incidents} careCenters={careCenters} vehicles={vehicles} dangerZones={dangerZones} drawings={drawings}
                    onFullscreen={() => setFsMapOpen(true)}
                />
                {isChef && <MainCouranteCard mainCourante={mainCourante} token={token} isChef={isChef} onRefresh={fetchData} />}
            </div>

            {isChef && <EngagedPersonnelSection engaged={engaged} />}

            {directory.length > 0 && <CrisisDirectoryCard directory={directory} />}

            <PersonnelDialog open={showShifts} onOpenChange={setShowShifts} engaged={engaged} />

            <DashboardFullscreenMap
                open={fsMapOpen} onClose={() => { setFsMapOpen(false); fetchData(); }} mapCenter={mapCenter} theme={theme} token={token}
                incidents={incidents} careCenters={careCenters} vehicles={vehicles} dangerZones={dangerZones}
            />
        </div>
    );
};

export default DashboardPage;
